// Supabase Panel - Panneau avec navigation sidebar
// Affiche Database, Utilisateurs, Stockage, Edge Functions, et Logs

import { useState } from 'react';
import {
    Database,
    Terminal,
    Zap,
    LayoutGrid,
    Users,
    HardDrive,
    ArrowLeft
} from 'lucide-react';
import { SupabaseSchemaViewer } from './SupabaseSchemaViewer';
import { SupabaseLogsViewer } from './SupabaseLogsViewer';
import { SupabaseEdgeFunctionsManager } from './SupabaseEdgeFunctionsManager';
import { SupabaseOverview } from './SupabaseOverview';
import { useSupabaseMCP } from '@/hooks/useSupabaseMCP';
import { Button } from '@/components/ui/button';
import { PxMark } from '@/components/PxMark';
import { cn } from '@/lib/utils';

type SectionType = 'overview' | 'database' | 'users' | 'storage' | 'functions' | 'logs';

interface SupabasePanelProps {
    onClose?: () => void;
    defaultSection?: SectionType;
    pixelProjectId?: string;
}

interface NavItem {
    id: SectionType;
    label: string;
    icon: typeof Database;
    implemented: boolean;
}

const navItems: NavItem[] = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: LayoutGrid, implemented: true },
    { id: 'database', label: 'Base de données', icon: Database, implemented: true },
    { id: 'users', label: 'Utilisateurs', icon: Users, implemented: false },
    { id: 'storage', label: 'Stockage', icon: HardDrive, implemented: false },
    { id: 'functions', label: 'Edge Functions', icon: Zap, implemented: true },
    { id: 'logs', label: 'Logs', icon: Terminal, implemented: true },
];

export function SupabasePanel({ onClose, defaultSection = 'overview', pixelProjectId }: SupabasePanelProps) {
    const { isConnected, selectedProjectName } = useSupabaseMCP(pixelProjectId);
    const [activeSection, setActiveSection] = useState<SectionType>(defaultSection);

    return (
        <div className="flex h-full flex-col bg-background">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border bg-card px-4 py-3">
                <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center border border-border bg-secondary">
                        <Database className="h-4 w-4 text-foreground" />
                    </div>
                    <span className="text-[13px] font-medium text-foreground">Supabase</span>
                    <span
                        className={cn(
                            'px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em]',
                            isConnected ? 'bg-ok/10 text-ok' : 'bg-secondary text-faint'
                        )}
                    >
                        {isConnected ? 'Connecté' : 'Non connecté'}
                    </span>
                </div>
                <div className="font-mono text-[11px] text-muted-foreground">
                    {isConnected ? selectedProjectName || 'Projet Supabase' : ''}
                </div>
                {onClose && (
                    <Button variant="ghost" size="sm" onClick={onClose} className="gap-2 text-muted-foreground hover:text-foreground">
                        <ArrowLeft className="h-4 w-4" />
                        Retour à la préview
                    </Button>
                )}
            </div>

            {/* Main content with sidebar */}
            <div className="flex flex-1 overflow-hidden">
                {/* Sidebar Navigation */}
                <nav className="w-48 flex-shrink-0 border-r border-border bg-card py-2">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => setActiveSection(item.id)}
                            className={cn(
                                "flex w-full items-center gap-3 px-4 py-2 text-left text-[13px] transition-colors",
                                activeSection === item.id
                                    ? "bg-secondary font-medium text-foreground"
                                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                            )}
                        >
                            <item.icon className="h-4 w-4 flex-shrink-0" />
                            <span className="truncate">{item.label}</span>
                            {!item.implemented && (
                                <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.08em] text-faint">
                                    Bientôt
                                </span>
                            )}
                        </button>
                    ))}
                </nav>

                {/* Content Area */}
                <div className="flex-1 overflow-hidden">
                    {activeSection === 'overview' && <SupabaseOverview pixelProjectId={pixelProjectId} onNavigate={setActiveSection} />}
                    {activeSection === 'database' && <SupabaseSchemaViewer pixelProjectId={pixelProjectId} />}
                    {activeSection === 'users' && <NotImplementedSection title="Utilisateurs" description="La gestion des utilisateurs et l'authentification seront disponibles prochainement." />}
                    {activeSection === 'storage' && <NotImplementedSection title="Stockage" description="La gestion des fichiers et buckets sera disponible prochainement." />}
                    {activeSection === 'functions' && <SupabaseEdgeFunctionsManager pixelProjectId={pixelProjectId} />}
                    {activeSection === 'logs' && <SupabaseLogsViewer pixelProjectId={pixelProjectId} />}
                </div>
            </div>
        </div>
    );
}

// Component for features not yet implemented
function NotImplementedSection({ title, description }: { title: string; description: string }) {
    return (
        <div className="flex h-full flex-col items-center justify-center p-8 text-center">
            <PxMark state="idle" size={24} className="mb-4" />
            <h2 className="mb-2 text-[16px] font-medium text-foreground">{title}</h2>
            <p className="max-w-md text-sm text-muted-foreground">
                {description}
            </p>
            <span className="mt-4 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] bg-secondary text-faint">
                Bientôt
            </span>
        </div>
    );
}

export default SupabasePanel;
